import { IEventRequestParams, IPaginationParams, ISortRequestParams } from './shared.dto';

abstract class IReportsDAO {
    public abstract getReports(params: IGetReportsRequestParams): Promise<IReportsListDTO>;
    public abstract deleteReport(params: IEventRequestParams & { assetMetadataId: number }): Promise<void>;
    public abstract updateReport(params: IEventRequestParams & IUpdateReportDTO & { assetMetadataId: number }): Promise<IReportDetailsDTO>;
    public abstract createReport(params: ICreateReportDTO & IEventRequestParams): Promise<void>;
    public abstract getReportFile(url: string, fileName: string): Promise<void>;
    public abstract getReportFileDownloadUrl(params: IEventRequestParams & { assetMetadataId: number }): Promise<IReportFileDownloadUrlDTO>;
    public abstract generateEventRegistrationReport(params: IGenerateEventReportParams): Promise<IReportListItemDTO>;
    public abstract generateEventInteractionsReport(params: IGenerateEventReportParams): Promise<IReportListItemDTO>;
    public abstract generateAttendeeInteractionsReport(
        params: IGenerateEventReportParams,
        meetingIds: number[],
        layoutType: ReportLayout
    ): Promise<IReportListItemDTO>;
    public abstract generateRegistrantsBySessionReport(params: IGenerateEventReportParams): Promise<IReportListItemDTO>;
    public abstract generateMeetingInteractionsReport(params: IGenerateEventReportParams, meetingIds: number[]): Promise<IReportListItemDTO>;
    public abstract generateMeetingRegistrationReport(params: IGenerateEventReportParams, meetingIds: number[]): Promise<IReportListItemDTO>;
    public abstract getMeetingsReportStartDateTimes(params: IEventRequestParams): Promise<IStartDateTimesDTO>;
    public abstract getDefaultEventReportConfiguration(params: IConfigurationParams): Promise<IReportConfigItem[]>;
    public abstract getConfigurations(params: IConfigurationParams): Promise<IReportConfiguration[]>;
    public abstract saveConfiguration(params: ISaveConfigurationParams): Promise<IReportConfiguration>;
    public abstract deleteConfiguration(params: IEventRequestParams & { reportConfigurationTemplateId: number}): Promise<IReportConfiguration>;
}

interface IReportListItemDTO {
    assetMetadataId: number;
    assetType: ReportType;
    eventId: number;
    size: number;
    alias: string;
    uploadDateTime: Date;
    uploadedBy: string;
    objectUrl: string;
    assetName: string;
    status: ReportStatus;
}

type IReportDetailsDTO = IReportListItemDTO;

type TGetReportsSortKeys = 'uploadDateTime';

type IGetReportsRequestParams =
    IEventRequestParams &
    ISortRequestParams<TGetReportsSortKeys> &
    IPaginationParams;


interface ICreateReportDTO {
    reportFile: File;
    assetType: ReportType;
    size: number;
    alias: string;
    assetName: string;
}

interface IUpdateReportDTO {
    assetMetadataId: number;
    assetType: ReportType;
    alias: string;
}

type IReportsListDTO = IReportListItemDTO[];

enum ReportType {
    EVENT_REPORT = 'EVENT_REPORT',
    PRE_EVENT_DATA = 'PRE_EVENT_DATA',
    ZOOM_WEBINAR_REGISTRATION = 'ZOOM_WEBINAR_REGISTRATION',
    ZOOM_WEBINAR_ATTENDEE = 'ZOOM_WEBINAR_ATTENDEE',
    MEETING_INTERACTIONS_REPORT = 'MEETING_INTERACTIONS_REPORT',
    EVENT_INTERACTIONS_REPORT = 'EVENT_INTERACTIONS_REPORT',
    ATTENDEE_INTERACTIONS_REPORT = 'ATTENDEE_INTERACTIONS_REPORT',
    REGISTRANTS_BY_SESSION_REPORT = 'REGISTRANTS_BY_SESSION_REPORT',
    MEETING_REGISTRATION_REPORT = 'MEETING_REGISTRATION_REPORT',
    EVENT_REGISTRATION_REPORT = 'EVENT_REGISTRATION_REPORT',
    ZOOM_MEETING_REGISTRATION_REPORT = 'ZOOM_MEETING_REGISTRATION_REPORT',
    OTHER_REPORT = 'OTHER_REPORT'
}

enum ReportStatus {
    PENDING = 'PENDING',
    READY = 'READY'
}

interface IAssetUrlDTO {
    assetUrl: string;
    objectKey: string;
}

interface IUpdateReportSubscriptionResponse {
    value: {
        data: {
            onUpdateAssetMetadata: IReportListItemDTO;
        };
    };
}

interface IStartDateTimesDTO {
    dates: string[];
}

interface ICreateEventReportDTO {
    startDateTime?: string;
    endDateTime?: string;
}

enum Category {
    MEETING = 'Meeting',
    ATTENDEE = 'Attendee',
    INTERACTION = 'Interaction',
    CUSTOM = 'Custom'
}

enum GenerateConfigurationTab {
    FILTERS = 'FILTERS',
    COLUMNS = 'COLUMNS'
}
interface IReportConfigItem {
    orderIndex: number;
    label: string;
    enabled: boolean;
    fieldId: string;
    customField: boolean;
    category?: Category;
}
interface IMeetingsReportItem {
    id: string;
    startDateTime: Date;
    type: string;
    topic: string;
    track: string;
    presenters: string[];
}

type IGenerateEventReportParams = IEventRequestParams & ICreateEventReportDTO & { fields: IReportConfigItem[] };

type ISaveConfigurationParams = IEventRequestParams & IReportConfiguration;

type IConfigurationParams = IEventRequestParams & { type: ReportType | 'ATTENDEE_INTERACTIONS_REPORT_V2' } & { layoutType?: ReportLayout };

interface IReportConfiguration {
    reportConfigurationTemplateId?: number;
    type: ReportType;
    name: string;
    columns: IReportConfigItem[];
    filters: IConfigurationFilter[];
    target: ReportConfigurationTarget;
    layoutType?: ReportLayout;
}

enum ReportConfigurationTarget {
    USER = 'USER',
    ACCOUNT = 'ACCOUNT'
}

enum ReportLayout {
    ROW_PER_ATTENDEE = 'ROW_PER_ATTENDEE',
    ROW_PER_ATTENDEE_PER_MEETING = 'ROW_PER_ATTENDEE_PER_MEETING'
}

enum LocalConfigurationStore {
    EVENT_INTERACTION = 'EVENT_INTERACTION_GENERATION_CONFIGURATION',
    REGISTRANT_BY_SESSION = 'REGISTRANT_BY_SESSION_GENERATION_CONFIGURATION',
    ATTENDEE_INTERACTION = 'ATTENDEE_INTERACTION_GENERATION_CONFIGURATION',
    EVENT_REGISTRATION = 'EVENT_REGISTRATION_GENERATION_CONFIGURATION',
}

enum ConfigurationFilterType {
    DATE = 'DATE',
    CHECKBOX = 'CHECKBOX',
    MEETINGS = 'MEETINGS',
    SEARCH = 'SEARCH',
    NUMBER = 'NUMBER',
    DATE_TIME = 'DATE_TIME'
}

enum FilterField {
    MEETING_DATE = 'MEETING_DATE',
    ATTENDEE_MEETING_ROLE = 'ATTENDEE_MEETING_ROLE',
    MEETING_ID = 'MEETING_ID',
    MEETING_ACCESS = 'MEETING_ACCESS',
    MEETING_PROGRESS = 'MEETING_PROGRESS',
    MEETING_STATUS = 'MEETING_STATUS',
    MEETING_TOPIC = 'MEETING_TOPIC',
    MEETING_TRACK = 'MEETING_TRACK',
    MEETING_TYPE ='MEETING_TYPE',
    ATTENDANCE_METHOD = 'ATTENDANCE_METHOD',
    VERIFIED_ATTENDEE = 'VERIFIED_ATTENDEE',
    ATTENDEE_JOINING_METHOD = 'ATTENDEE_JOINING_METHOD',
    JOINED_LIVE = 'JOINED_LIVE',
    LATE = 'LATE',
    MEETING_ATTENDEE_ISSUES = 'MEETING_ATTENDEE_ISSUES',
    MEETING_ATTENDEE_STATUS = 'MEETING_ATTENDEE_STATUS',
    ATTENDEE_ORGANIZATION_NAME = 'ATTENDEE_ORGANIZATION_NAME',
    ATTENDEE_FIRST_NAME = 'ATTENDEE_FIRST_NAME',
    ATTENDEE_LAST_NAME = 'ATTENDEE_LAST_NAME',
    ATTENDEE_PHONE_NUMBER = 'ATTENDEE_PHONE_NUMBER',
    ATTENDEE_TITLE = 'ATTENDEE_TITLE',
    INTERACTION_DURATION = 'INTERACTION_DURATION',
    ATTENDEE_EMAIL_ADDRESS = 'ATTENDEE_EMAIL_ADDRESS',
    REGISTRATION_DATE_AND_TIME= 'REGISTRATION_DATE_AND_TIME',
    REGISTRATION_STATUS = 'REGISTRATION_STATUS',
    OPERATOR_SHARED_SCREEN = 'OPERATOR_SHARED_SCREEN',
}

interface IConfigurationFilter {
    operator?: IReportOperator;
    field: string;
    values: string[];
    customField?: boolean;
}

type IReportOperator = 'EQUALS' |
    'NOT_EQUALS' |
    'STARTS_WITH' |
    'NOT_STARTS_WITH' |
    'ENDS_WITH' |
    'NOT_ENDS_WITH' |
    'CONTAINS' |
    'NOT_CONTAINS' |
    'IN' |
    'NOT_IN' |
    'IS_NULL' |
    'IS_NOT_NULL' |
    'BEFORE' |
    'AFTER' |
    'BETWEEN' |
    'MORE_THAN' |
    'LESS_THAN';

interface ISelectableFilter {
    type: ConfigurationFilterType;
    field: string;
    label: string;
    items?: IReportFilterItem[];
    placeholder?: string;
    additionalLabel?: string;
    defaultOperator?: IReportOperator;
    excludeFromSave?: boolean;
    isRequired?: boolean;
}

interface IReportOperatorItem {
    label: string;
    value: IReportOperator;
}

interface IReportFilterItem {
    label?: string;
    value: string;
}

interface IReportFileDownloadUrlDTO {
    downloadUrl: string;
}

export {
    IReportsListDTO,
    ICreateReportDTO,
    IReportListItemDTO,
    IReportDetailsDTO,
    IGetReportsRequestParams,
    IReportsDAO,
    ReportType,
    ReportStatus,
    TGetReportsSortKeys,
    IAssetUrlDTO,
    IUpdateReportDTO,
    IUpdateReportSubscriptionResponse,
    IStartDateTimesDTO,
    IReportConfigItem,
    ICreateEventReportDTO,
    IGenerateEventReportParams,
    IReportConfiguration,
    ISaveConfigurationParams,
    ReportConfigurationTarget,
    LocalConfigurationStore,
    IConfigurationParams,
    ConfigurationFilterType,
    IConfigurationFilter,
    Category,
    GenerateConfigurationTab,
    FilterField,
    IMeetingsReportItem,
    ISelectableFilter,
    IReportOperatorItem,
    IReportFilterItem,
    IReportOperator,
    ReportLayout,
    IReportFileDownloadUrlDTO
};
